import { either, function as function_ } from "fp-ts";
import { z } from "zod";
import { create } from "zustand";
import type { StorageValue } from "zustand/middleware";
import { devtools, persist } from "zustand/middleware";

import { zSchema } from "./entity";

import { zFromJson } from "~/shared/zod";
import { createSelectors } from "~/shared/zustand";

const zOrder = zSchema.extend({
  moveIsScheduledFor: z.coerce.date(),
  createdAt: z.coerce.date(),
});
type Order = z.infer<typeof zOrder>;

const zState = z.object({
  orders: z.array(zOrder),
});
type State = z.infer<typeof zState>;

const zStore = z.object({
  state: zState,
  version: z.number().optional(),
});

const initialState: State = {
  orders: [],
};

const STORE_NAME = "MyOrdersStore";

const useStore = createSelectors(
  create<State>()(
    persist(
      devtools(() => initialState, {
        store: STORE_NAME,
      }),
      {
        name: STORE_NAME,
        storage: {
          getItem: (
            name: string,
          ): StorageValue<State> | Promise<StorageValue<State> | null> | null => {
            return function_.pipe(
              localStorage.getItem(name),
              either.fromNullable(null),
              either.map((storedValue) => zFromJson(zStore).safeParse(storedValue)),
              either.flatMap(({ success, data, error }) =>
                success ? either.right(data) : either.left(error),
              ),
              either.mapLeft(() => null),
              either.map(({ state, version }) => ({
                state,
                ...(typeof version === "number" && {
                  version,
                }),
              })),
              either.toUnion,
            );
          },
          setItem: (name: string, value: StorageValue<State>): unknown => {
            return localStorage.setItem(name, JSON.stringify(value));
          },
          removeItem: (name: string): unknown => {
            return localStorage.removeItem(name);
          },
        },
      },
    ),
  ),
);

function add(value: Order) {
  useStore.setState(({ orders }) => ({
    orders: [...orders, value],
  }));
}

function remove(id: Order["id"]) {
  useStore.setState(({ orders }) => ({
    orders: orders.filter((value) => value.id !== id),
  }));
}

function clear() {
  useStore.setState(() => ({
    orders: [],
  }));
}

export { add, clear, remove, useStore };
